import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import {
  loadLeadSourceTagsSuccess,
  loadLeadSourceTagsFailure,
  loadLeadSourceTags,
} from '../actions/load-lead-source-tags.actions';
import { LeadSourceTag } from '@shared/models/lead-source-tag';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { LoadingState } from '@shared/types/loading-state';
import {
  createLeadSourceTag,
  createLeadSourceTagFailure,
  createLeadSourceTagSuccess,
} from '../actions/create-lead-source-tag.actions';
import { LeadSource } from '@shared/types/lead-source';

export const featureKey = 'lead-source-tags';

export const leadSourceTagsAdapter = createEntityAdapter<LeadSourceTag>({
  selectId: (leadSourceTag: LeadSourceTag) => leadSourceTag.id,
});

export interface State extends EntityState<LeadSourceTag> {
  loadingState: LoadingState;
}

export const initialState: State = leadSourceTagsAdapter.getInitialState({
  loadingState: 'not_loading' as LoadingState,
});

export const leadSourceTagsReducer = createReducer(
  initialState,
  on(loadLeadSourceTags, createLeadSourceTag, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
  })),
  on(createLeadSourceTagSuccess, (state, { leadSourceTag }) =>
    leadSourceTagsAdapter.upsertOne(leadSourceTag, {
      ...state,
      loadingState: 'loaded' as LoadingState,
    }),
  ),
  on(loadLeadSourceTagsSuccess, (state, { leadSourceTags }) =>
    leadSourceTagsAdapter.upsertMany(leadSourceTags, {
      ...state,
      loadingState: 'loaded' as LoadingState,
    }),
  ),
  on(loadLeadSourceTagsFailure, createLeadSourceTagFailure, (state) => ({
    ...state,
    loadingState: 'not_loading' as LoadingState,
  })),
);

export function reducer(state: State | undefined, action: any) {
  return leadSourceTagsReducer(state, action);
}

export const leadSourceTagsFeatureSelector = createFeatureSelector<State>(featureKey);

const { selectAll } = leadSourceTagsAdapter.getSelectors(leadSourceTagsFeatureSelector);

export const selectAllLeadSourceTags = selectAll;

export const selectLeadSourceTagsForLeadSource = (leadSource: LeadSource, searchTerm = '') =>
  createSelector(selectAllLeadSourceTags, (leadSourceTags) =>
    leadSourceTags
      .filter((leadSourceTag) => leadSourceTag.attributes.lead_source === leadSource)
      .filter((leadSourceTag) => leadSourceTag.attributes.name.toLowerCase().includes(searchTerm.trim().toLowerCase()))
      .sort((firstTag, secondTag) => secondTag.attributes.created_at.localeCompare(firstTag.attributes.created_at)),
  );

export const selectLeadSourceTagLoadingState = createSelector(
  leadSourceTagsFeatureSelector,
  (state: State) => state.loadingState,
);
